/**
 * PromptDisplay - The Intelligent Standard
 *
 * Compact inline prompt display with elegant popover for full text.
 */

import React from 'react';

export interface PromptDisplayProps {
  promptText: string;
  statusMessage?: string;
}

export function PromptDisplay({
  promptText,
  statusMessage,
}: PromptDisplayProps) {
  const [showPopover, setShowPopover] = React.useState(false);
  const [showScrollHint, setShowScrollHint] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Truncate to ~150 characters for inline display
  const truncatedText =
    promptText.length > 150
      ? promptText.slice(0, 150).trim() + ' ...'
      : promptText;

  const needsMore = promptText.length > 150;

  React.useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current) {
        const hasOverflow =
          contentRef.current.scrollHeight > contentRef.current.clientHeight;
        setShowScrollHint(hasOverflow);
      }
    };

    if (showPopover) {
      checkOverflow();
      window.addEventListener('resize', checkOverflow);
      return () => window.removeEventListener('resize', checkOverflow);
    }
  }, [showPopover, promptText]);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 10;
      if (isNearBottom) {
        setShowScrollHint(false);
      }
    }
  };

  // Close popover on escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showPopover) {
        setShowPopover(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showPopover]);

  return (
    <>
      <div className="space-y-2">
        {/* Header row with label */}
        <span
          className="text-xs uppercase tracking-widest font-semibold"
          style={{ color: '#A0A0A0', letterSpacing: '0.1em' }}
        >
          PROMPT
        </span>

        {/* Prompt text with vertical accent bar */}
        <div className="flex gap-3">
          <div
            className="w-1 flex-shrink-0 rounded"
            style={{ background: '#8B2332' }}
          />
          <p
            className="font-serif text-[1.0625rem] leading-relaxed m-0"
            style={{ color: '#2A2A2A', fontWeight: 500 }}
          >
            {truncatedText}
            {needsMore && (
              <>
                {' '}
                <button
                  onClick={() => setShowPopover(true)}
                  className="text-sm font-medium transition-colors inline"
                  style={{ color: '#8B2332' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.textDecoration = 'underline';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.textDecoration = 'none';
                  }}
                >
                  More
                </button>
              </>
            )}
          </p>
        </div>
      </div>

      {statusMessage && (
        <div
          className="px-3 py-1.5 rounded-md text-xs font-medium"
          style={{
            background: 'rgba(139, 35, 50, 0.08)',
            border: '1px solid rgba(139, 35, 50, 0.2)',
            color: '#8B2332',
          }}
        >
          {statusMessage}
        </div>
      )}

      {/* Elegant Popover */}
      {showPopover && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-50 flex items-start justify-center pt-24 px-4"
            style={{ background: 'rgba(139, 35, 50, 0.2)' }}
            onClick={() => setShowPopover(false)}
          >
            {/* Popover Card */}
            <div
              className="relative max-w-3xl w-full rounded-2xl overflow-hidden"
              style={{
                background: '#FDFCFB',
                border: '1px solid #E8E0D8',
                boxShadow:
                  '0 20px 60px rgba(139, 35, 50, 0.15), 0 8px 24px rgba(0, 0, 0, 0.08)',
                animation: 'promptPopoverIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header with accent bar */}
              <div
                className="px-8 py-5 flex items-center justify-between"
                style={{
                  background:
                    'linear-gradient(135deg, #8B2332 0%, #6B1A27 100%)',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-1 h-6 rounded"
                    style={{ background: 'rgba(255, 255, 255, 0.6)' }}
                  />
                  <h3
                    className="text-sm uppercase tracking-widest font-semibold m-0"
                    style={{ color: '#FFFFFF', letterSpacing: '0.15em' }}
                  >
                    PROMPT
                  </h3>
                </div>
                <button
                  onClick={() => setShowPopover(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-full transition-all"
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background =
                      'rgba(255, 255, 255, 0.2)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background =
                      'rgba(255, 255, 255, 0.1)';
                  }}
                  aria-label="Close"
                >
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M12 4L4 12M4 4l8 8"
                      stroke="#FFFFFF"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="relative">
                <div
                  ref={contentRef}
                  onScroll={handleScroll}
                  className="px-8 py-7 max-h-[500px] overflow-y-auto scrollbar-custom"
                  style={{ background: '#FDFCFB' }}
                >
                  <p
                    className="m-0 whitespace-pre-wrap leading-[1.8] font-serif text-[1.0625rem]"
                    style={{ color: '#2A2A2A', fontWeight: 500 }}
                  >
                    {promptText}
                  </p>
                </div>

                {/* Scroll hint */}
                {showScrollHint && (
                  <>
                    <div
                      className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
                      style={{
                        background:
                          'linear-gradient(to top, #FDFCFB 0%, rgba(253, 252, 251, 0.95) 40%, transparent 100%)',
                      }}
                    />
                    <div
                      className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 text-xs rounded-full pointer-events-none font-semibold shadow-lg"
                      style={{
                        background: '#8B2332',
                        color: '#FFFFFF',
                        letterSpacing: '0.05em',
                      }}
                    >
                      ↓ Scroll for more
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @keyframes promptPopoverIn {
          from {
            opacity: 0;
            transform: translateY(-8px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .scrollbar-custom::-webkit-scrollbar {
          width: 8px;
        }
        .scrollbar-custom::-webkit-scrollbar-track {
          background: #F5F0EB;
          border-radius: 4px;
        }
        .scrollbar-custom::-webkit-scrollbar-thumb {
          background: #D4C4B8;
          border-radius: 4px;
        }
        .scrollbar-custom::-webkit-scrollbar-thumb:hover {
          background: #C4B4A8;
        }
        .scrollbar-custom {
          scrollbar-width: thin;
          scrollbar-color: #D4C4B8 #F5F0EB;
        }
      `}</style>
    </>
  );
}
